import type { RoleKey } from "@appcaire/ui";
import type { LandingLocale } from "../landingCopy";

/**
 * Brand 2.2 landing copy: the four-layer content model (Beviljat →
 * Genomförandeplan → Planerat → Utfört), the "Jag är…" roles and the society
 * band. The same breakfast and cleaning visit is followed through all four
 * layers. Rules: human needs before service visits; a service visit has a
 * fixed day and moves only as an exception; unperformed granted time goes to
 * the time pool. Numbers are an example scenario, except 5–10 % (owner-supplied).
 */
interface LayerCopy {
  label: string;
  title: string;
  text: string;
  /** How often the layer changes and who changes it: the flow over time. */
  cadence: string;
  items: { name: string; value: string }[];
  note: string;
}

/** One of the three planning flows; all start from the care plans. */
interface FlowCopy {
  label: string;
  title: string;
  text: string;
}

/** One hero benefit: a figure or short claim and a label. */
interface BenefitCopy {
  figure: string;
  label: string;
}

/** Labels and the two acts of the dashboard ↔ apps scene. */
export interface SyncSceneCopy {
  label: string;
  employee: string;
  planner: string;
  client: string;
  /** Act 1: the planner changes the visit; both apps get it. */
  plannerChange: string;
  employeeGetsChange: string;
  clientGetsChange: string;
  /** Act 2: the employee's GPS says late; the dashboard and the client get it. */
  employeeLate: string;
  plannerGetsEta: string;
  clientGetsEta: string;
  steps: [string, string];
}

export interface BrandHomeCopy {
  benefits: {
    title: string;
    items: [BenefitCopy, BenefitCopy, BenefitCopy, BenefitCopy];
    footnote: string;
  };
  layers: {
    eyebrow: string;
    cadenceLabel: string;
    title: string;
    lead: string;
    items: [LayerCopy, LayerCopy, LayerCopy, LayerCopy];
    pool: { title: string; text: string; value: string; valueLabel: string };
    priority: { title: string; text: string };
    shared: string;
    /** The animated scene under the four layers: one plan in the calendar and both apps. */
    sync: SyncSceneCopy;
    link: string;
    example: string;
  };
  flows: {
    eyebrow: string;
    title: string;
    lead: string;
    items: [FlowCopy, FlowCopy, FlowCopy];
    channels: string;
  };
  roles: {
    eyebrow: string;
    title: string;
    groupLabel: string;
    items: {
      role: RoleKey;
      label: string;
      title: string;
      text: string;
      checks: string[];
    }[];
  };
  society: {
    figure: string;
    text: string;
    source: string;
    sourceLink: string;
    sourceHref: string;
    note: string;
    link: string;
  };
}

const sv: BrandHomeCopy = {
  benefits: {
    title: "Vad modern besöksplanering ger",
    items: [
      {
        figure: "−90 %",
        label: "Upp till 90 % mindre tid för planering och dagliga ändringar",
      },
      {
        figure: "I tid",
        label: "Besöken startar i tid, med kontinuitet som du styr",
      },
      {
        figure: "+5 %",
        label: "Högre brukartidsandel",
      },
      {
        figure: "+5–10 %",
        label: "På sista raden. Nöjdare medarbetare och kunder.",
      },
    ],
    footnote:
      "Upp till-värden. Utfallet beror på verksamhetens underlag och regler.",
  },
  layers: {
    eyebrow: "Så funkar det",
    cadenceLabel: "Hur ofta",
    title: "Från beslut till besök. Samma plan hela vägen.",
    lead: "Det som i dag finns i planerarens huvud finns i Caire. Därför stämmer beslut, plan och verklighet.",
    items: [
      {
        label: "Kommunens beslut",
        title: "Beviljad tid",
        text: "Vilken insats, hur länge och hur ofta. Ingen veckodag och inget klockslag än.",
        cadence:
          "Ungefär en gång per år, och när en ny brukare kommer in. Byter brukaren utförare följer beslutet oftast med; det är bara uppdraget som går till en ny utförare.",
        items: [
          { name: "Frukost", value: "30 min · varje dag" },
          { name: "Städ", value: "90 min · varannan vecka" },
        ],
        note: "bara behov, ingen tid",
      },
      {
        label: "Brukare + utförare",
        title: "Genomförandeplan",
        text: "Ett veckomönster som ni gör tillsammans: veckodag, önskad tid och flex. Inga datum.",
        cadence:
          "Görs i samband med beslutet, och ofta på nytt när brukaren byter utförare. Brukaren och utföraren ändrar den när behoven ändras, så länge den ryms i beslutet.",
        items: [
          { name: "Frukost", value: "varje dag · 08:00 ±15 · 30 min" },
          { name: "Städ", value: "tisdagar varannan v. · 14:30 ±45 · 90 min" },
        ],
        note: "brukaren är med och bestämmer",
      },
      {
        label: "Planeraren",
        title: "Planerade besök",
        text: "Besöksplaneringen gör mönstret till riktiga besök på riktiga datum, inom personalens arbetspass, med restid och rätt person.",
        cadence:
          "Löpande. Caire sköter besöksplaneringen automatiskt utifrån genomförandeplanerna och arbetspassen, och uppdaterar den när verkligheten ändras.",
        items: [
          { name: "Frukost", value: "mån 5 okt · 08:09–08:39 · Anna" },
          { name: "Städ", value: "tis 6 okt · 14:10–15:40" },
        ],
        note: "restiden finns redan med",
      },
      {
        label: "Medarbetaren",
        title: "Utförda besök",
        text: "Målet är samma som planerat. Ändras något ser medarbetaren och brukaren det direkt i appen.",
        cadence:
          "Varje dag. Det som händer i vardagen förs tillbaka in i planen, så att besöken stämmer med planeringen när de utförs.",
        items: [
          { name: "Frukost", value: "08:09–08:39 · som planerat" },
          { name: "Städ", value: "14:10–15:20 · 20 min till tidspoolen" },
        ],
        note: "alla vet, hela tiden",
      },
    ],
    pool: {
      title: "Tidspoolen: beviljad tid går aldrig förlorad",
      text: "Blir en serviceinsats kortare sparas den beviljade tiden i en pool och planeras in senare. Brukaren får hela sin beviljade tid, och utföraren får betalt för genomförandeplanen.",
      value: "+20 min",
      valueLabel: "städ · att planera in",
    },
    priority: {
      title: "Tidsbundna behov går först",
      text: "Personliga behov som hör till en viss tid på dagen går alltid först: uppstigning, påklädning, förflyttning, medicin, måltider, toalett, dusch och ledsagning till läkarbesök. Serviceinsatser som städ, tvätt, inköp och promenad styrs av hur ofta de ska ske. De har en fast dag och flyttas bara undantagsvis vid personalbrist, inom beslutets fönster.",
    },
    shared:
      "Brukare, närstående och personal ser samma plan, och varför den ändras.",
    sync: {
      label:
        "Exempel: samma plan i planerarens kalender och i apparna hos medarbetare och brukare",
      employee: "Medarbetarens app",
      planner: "Planerarens kalender",
      client: "Brukarens app",
      plannerChange: "Planeraren flyttar Karin Ekströms besök till 10.30",
      employeeGetsChange: "Ändrat: Karin Ekström 10.30–11.00",
      clientGetsChange: "Anna kommer ca kl 10.30",
      employeeLate: "GPS: 12 min försenad · ny ankomst 10.42",
      plannerGetsEta: "Ny ankomsttid från appen: Anna 10.42",
      clientGetsEta: "Anna är lite försenad och kommer ca kl 10.42",
      steps: [
        "Planeraren ändrar i kalendern. Medarbetaren och brukaren ser det direkt i appen.",
        "Medarbetarens app märker att hon blir sen. Kalendern och brukarens app får den nya tiden.",
      ],
    },
    link: "Läs hur det funkar",
    example: "Exempelscenario",
  },
  flows: {
    eyebrow: "Tre flöden",
    title: "Samma genomförandeplaner. Tre sätt att planera.",
    lead: "Allt utgår från genomförandeplanerna och besöksplaneringen: när en ny brukare kommer in, när veckorna framåt planeras och när dagen ändras.",
    items: [
      {
        label: "Ny brukare",
        title: "Ny brukare och genomförandeplan",
        text: "Lägg in brukaren och besöken i genomförandeplanen. Innan något sparas söker Caire fram veckodag och tid som fungerar med brukarens behov, rutterna och de andra brukarna. Planeraren väljer.",
      },
      {
        label: "Förplanering",
        title: "Veckorna framåt",
        text: "Besöksplaneringen och behovet av arbetspass planeras löpande veckor i förväg, utifrån genomförandeplanerna. Caire tar fram förslag, planeraren jämför och publicerar.",
      },
      {
        label: "Realtid",
        title: "När dagen ändras",
        text: "Sjukanmälan, avbokning, försening eller ett nytt besök: Caire hittar ersättare på sekunder. Besöken och rutterna räknas om från den publicerade planen, med restider efter aktuell trafik när trafikanpassning är påslagen. Det som redan är gjort ligger kvar, och alla berörda får besked i appen.",
      },
    ],
    channels:
      "Samma plan för varje roll: webben för kontoret, appen för medarbetare, brukare och anhöriga, Fråga CAIRE för frågor och förslag, och MCP för AI-assistenter som tillval.",
  },
  roles: {
    eyebrow: "Vad betyder det för mig?",
    title: "Jag är…",
    groupLabel: "Välj vem du är",
    items: [
      {
        role: "brukare",
        label: "Brukaren",
        title: "Samma välkända ansikten, på tiden ni kommit överens om.",
        text: "Kontinuitet planeras in, så du slipper berätta din historia för nya personer varje vecka.",
        checks: [
          "Färre nya ansikten",
          "Besök på utlovad tid",
          "Närstående kan följa med",
        ],
      },
      {
        role: "medarbetare",
        label: "Medarbetaren",
        title: "Rimliga rutter och mer tid hos brukaren.",
        text: "Mindre stress i bilen, en tydlig dag i appen och ändringar som kommer direkt, inte via sms.",
        checks: [
          "Dagens rutt i telefonen",
          "Rimliga restider",
          "Anteckningar på rätt besök",
        ],
      },
      {
        role: "planerare",
        label: "Planeraren",
        title: "Sjukanmälan kl 06:40? Löst innan kaffet är klart.",
        text: "Caire föreslår en ersättare som har rätt kompetens och känner brukarna. Du godkänner.",
        checks: [
          "Förslag på ersättare direkt",
          "Du godkänner alltid",
          "Hela veckan i en vy",
        ],
      },
      {
        role: "agare",
        label: "Ägaren",
        title: "Se var tiden går, planerat mot utfört.",
        text: "Brukartid, restid och kontinuitet i en rapport, med samma definitioner varje månad.",
        checks: [
          "Planerat mot utfört",
          "Var marginalen försvinner",
          "Rapport som PDF och Excel",
        ],
      },
      {
        role: "kommun",
        label: "Kommunen",
        title: "Jämförbar uppföljning för alla utförare.",
        text: "Samma definitioner och samma rapport, oavsett vem som utför insatsen.",
        checks: [
          "Gemensamma definitioner",
          "Spårbara siffror",
          "Samma rapport för alla utförare",
        ],
      },
      {
        role: "samhalle",
        label: "Samhället",
        title: "Varje sparad mil blir omsorg.",
        text: "Hemtjänsten är 5–10 % av kommunens budget och behovet växer. Bättre planering är skattepengar som går till människor.",
        checks: [
          "Mindre körning",
          "Mer omsorgstid per krona",
          "Hållbar hemtjänst",
        ],
      },
    ],
  },
  society: {
    figure: "5–10 %",
    text: "av kommunernas budget går till hemtjänst, och behovet växer varje år.",
    source:
      "Cirka 60 av kommunernas 800 miljarder kronor går till hemtjänst, utan särskilt boende och LSS.",
    sourceLink: "Källa och siffror per kommun: Sveriges Hemtjänst",
    sourceHref: "https://sverigeshemtjanst.se/data",
    note: "därför spelar varje minut roll",
    link: "För kommuner",
  },
};

const en: BrandHomeCopy = {
  benefits: {
    title: "What modern visit planning gives you",
    items: [
      {
        figure: "−90 %",
        label: "Up to 90 % less time on planning and daily changes",
      },
      {
        figure: "On time",
        label: "Visits start on time, with continuity you control",
      },
      {
        figure: "+5 %",
        label: "Higher client-time share",
      },
      {
        figure: "+5–10 %",
        label: "On the bottom line. Happier staff and clients.",
      },
    ],
    footnote: "Up-to figures. Results depend on your data and rules.",
  },
  layers: {
    eyebrow: "How it works",
    cadenceLabel: "How often",
    title: "From decision to visit. One plan all the way.",
    lead: "What lives in the planner's head today lives in Caire. So the decision, the plan and reality agree.",
    items: [
      {
        label: "Municipal decision",
        title: "Granted time",
        text: "Which service, how long and how often. No weekday and no time of day yet.",
        cadence:
          "About once a year, and when a new client starts. If the client changes provider the decision usually stays; only the assignment moves to the new provider.",
        items: [
          { name: "Breakfast", value: "30 min · every day" },
          { name: "Cleaning", value: "90 min · every other week" },
        ],
        note: "needs only, no times",
      },
      {
        label: "Client + provider",
        title: "Care plan",
        text: "A weekly pattern you agree together: weekday, preferred time and flex. No dates.",
        cadence:
          "Made together with the decision, and often anew when the client changes provider. The client and provider change it when needs change, as long as it fits the decision.",
        items: [
          { name: "Breakfast", value: "Every day · 08:00 ±15 · 30 min" },
          {
            name: "Cleaning",
            value: "Tuesdays, alternate weeks · 14:30 ±45 · 90 min",
          },
        ],
        note: "the client has a say",
      },
      {
        label: "The planner",
        title: "Planned visits",
        text: "Visit planning turns the pattern into real visits on real dates, within staff shifts, with travel and the right person.",
        cadence:
          "Continuously. Caire runs visit planning automatically from the care plans and staff shifts, and updates it as reality changes.",
        items: [
          { name: "Breakfast", value: "Mon 5 Oct · 08:09–08:39 · Anna" },
          { name: "Cleaning", value: "Tue 6 Oct · 14:10–15:40" },
        ],
        note: "travel is already included",
      },
      {
        label: "The caregiver",
        title: "Performed visits",
        text: "The goal is the same as planned. If anything changes, the caregiver and the client see it in the app.",
        cadence:
          "Every day. What happens in the day flows back into the plan, so visits match the planning when they are carried out.",
        items: [
          { name: "Breakfast", value: "08:09–08:39 · as planned" },
          { name: "Cleaning", value: "14:10–15:20 · 20 min to the time pool" },
        ],
        note: "everyone knows, all the time",
      },
    ],
    pool: {
      title: "The time pool: granted time is never lost",
      text: "If a service visit runs shorter, the granted time is saved in a pool and scheduled later. The client receives all their granted time, and the provider is paid for the care plan.",
      value: "+20 min",
      valueLabel: "cleaning · to schedule",
    },
    priority: {
      title: "Time-bound needs come first",
      text: "Personal needs tied to a time of day always come first: getting up, dressing, transfers, medication, meals, toileting, showering and escort to a doctor's appointment. Service visits such as cleaning, laundry, shopping and walks are set by how often they happen. They have a fixed day and move only as an exception when staff are short, within the decision's window.",
    },
    shared:
      "Clients, loved ones and staff see the same plan, and why it changes.",
    sync: {
      label:
        "Example: the same plan in the planner's calendar and in the staff and client apps",
      employee: "Staff app",
      planner: "Planner's calendar",
      client: "Client app",
      plannerChange: "The planner moves Karin Ekström's visit to 10:30",
      employeeGetsChange: "Changed: Karin Ekström 10:30–11:00",
      clientGetsChange: "Anna arrives around 10:30",
      employeeLate: "GPS: 12 min late · new arrival 10:42",
      plannerGetsEta: "New arrival time from the app: Anna 10:42",
      clientGetsEta: "Anna is running a little late and arrives around 10:42",
      steps: [
        "The planner changes the calendar. Staff and client see it in the app right away.",
        "The staff app notices she will be late. The calendar and the client app get the new time.",
      ],
    },
    link: "Read how it works",
    example: "Example scenario",
  },
  flows: {
    eyebrow: "Three flows",
    title: "The same care plans. Three ways to plan.",
    lead: "Everything starts from the care plans and visit planning: when a new client starts, when the weeks ahead are planned and when the day changes.",
    items: [
      {
        label: "New client",
        title: "New client and care plan",
        text: "Add the client and the visits in the care plan. Before anything is saved, Caire searches for a weekday and time that work with the client's needs, the routes and the other clients. The planner chooses.",
      },
      {
        label: "Pre-planning",
        title: "The weeks ahead",
        text: "Visit planning and the need for shifts are planned continuously weeks ahead, from the care plans. Caire prepares proposals; the planner compares and publishes.",
      },
      {
        label: "Real time",
        title: "When the day changes",
        text: "A sick call, a cancellation, a delay or a new visit: Caire finds cover in seconds. Visits and routes are recalculated from the published plan, with travel times from current traffic when traffic awareness is switched on. Work already done stays put, and everyone affected is told in the app.",
      },
    ],
    channels:
      "One plan for every role: the web app for the office, the app for caregivers, clients and families, Ask CAIRE for questions and proposals, and MCP for AI assistants as an option.",
  },
  roles: {
    eyebrow: "What does it mean for me?",
    title: "I am…",
    groupLabel: "Choose who you are",
    items: [
      {
        role: "brukare",
        label: "The client",
        title: "The same familiar faces, at the time you agreed.",
        text: "Continuity is planned in, so you don't have to tell your story to new people every week.",
        checks: [
          "Fewer new faces",
          "Visits at the promised time",
          "Loved ones can follow along",
        ],
      },
      {
        role: "medarbetare",
        label: "The caregiver",
        title: "Reasonable routes and more time with the client.",
        text: "Less stress in the car, a clear day in the app and changes that arrive at once, not by text message.",
        checks: [
          "Today's route on your phone",
          "Reasonable travel times",
          "Notes on the right visit",
        ],
      },
      {
        role: "planerare",
        label: "The planner",
        title: "Sick call at 06:40? Sorted before the coffee is ready.",
        text: "Caire proposes a replacement with the right skills who already knows the clients. You approve.",
        checks: [
          "A replacement proposal at once",
          "You always approve",
          "The whole week in one view",
        ],
      },
      {
        role: "agare",
        label: "The owner",
        title: "See where the time goes, planned versus performed.",
        text: "Client time, travel and continuity in one report, with the same definitions every month.",
        checks: [
          "Planned versus performed",
          "Where the margin disappears",
          "Report as PDF and Excel",
        ],
      },
      {
        role: "kommun",
        label: "The municipality",
        title: "Comparable follow-up for every provider.",
        text: "The same definitions and the same report, whoever delivers the service.",
        checks: [
          "Shared definitions",
          "Traceable figures",
          "One report for every provider",
        ],
      },
      {
        role: "samhalle",
        label: "Society",
        title: "Every kilometre saved becomes care.",
        text: "Home care is 5–10 % of a municipality's budget and the need is growing. Better planning means tax money that reaches people.",
        checks: [
          "Less driving",
          "More care time per krona",
          "Sustainable home care",
        ],
      },
    ],
  },
  society: {
    figure: "5–10 %",
    text: "of municipal budgets goes to home care, and the need grows every year.",
    source:
      "About SEK 60 billion of the municipalities' SEK 800 billion goes to home care, excluding residential care and LSS.",
    sourceLink: "Source and figures per municipality: Sveriges Hemtjänst",
    sourceHref: "https://sverigeshemtjanst.se/data",
    note: "that's why every minute matters",
    link: "For municipalities",
  },
};

export const brandHomeCopy: Record<LandingLocale, BrandHomeCopy> = { sv, en };
